/**
 * riskContextResolution.ts
 * Three-tier provenance resolution for trade risk context display.
 *
 * Determines which risk data to show on the TradeDrawer execution tab
 * and where it came from:
 *
 *   Tier 1 — "Pre-Trading Plan": the trade's account + strategy/playbook
 *            pair matches the session's confirmed risk snapshot.
 *   Tier 2 — "Account Default":  resolved asynchronously from the
 *            account's default risk profile (handled in Phase 2).
 *   Tier 3 — Empty state:        neither tier resolves → no block rendered.
 *
 * All functions in this module are intentionally pure — no DB calls, no
 * side effects — so they are safe to call from both React render cycles
 * and inside active Dexie transactions.
 *
 * @see riskComputationUtils.ts — cascade computation (different concern)
 * @see promotionReadiness.ts   — similar pure-utility pattern reference
 */

import type { RiskSpec } from '@/types/schema/AccountRisk';
import type {
  TradingSetupRiskSnapshot,
  RiskDerivationLevel,
  RiskDerivationStage,
} from '@/types/schema/TradingSetupSnapshots';

/**
 * Provenance label indicating how the displayed risk data was resolved.
 *
 * - `'pre-trading-plan'` — matched from the session's confirmed risk snapshot (Tier 1)
 * - `'account-default'`  — resolved from the account's default risk profile (Tier 2)
 */
export type RiskContextProvenance = 'pre-trading-plan' | 'account-default';

/**
 * Resolved risk context ready for UI rendering.
 *
 * Returned by Tier 1 (synchronous) and Tier 2 (async, Phase 2) resolvers.
 * A `null` result from either resolver represents Tier 3 (empty state —
 * no risk block is rendered).
 *
 * This is the final stable contract for T06 — Phase 1 defines the full
 * shape, Phase 1 implementation only produces the `'pre-trading-plan'`
 * variant, Phase 2 adds the `'account-default'` producer without
 * changing the contract.
 */
export interface ResolvedRiskContext {
  /** How the risk data was resolved */
  provenance: RiskContextProvenance;

  /** Effective risk specification to display */
  risk: RiskSpec;

  /** Display name of the risk profile for user context */
  profileName: string;

  /** Which cascade level produced the effective risk (Phase 5 derivation metadata) */
  derivedFromLevel?: RiskDerivationLevel;

  /** Whether the winning override came from profile or session (Phase 5 derivation metadata) */
  derivedFromStage?: RiskDerivationStage;

  /** ID of the per-override deviation note (resolved from winning override entry) */
  deviationNoteId?: string;

  /** Text content of the risk deviation note (loaded async, Tier 1 only) */
  deviationNoteText?: string;
}

/**
 * Resolve Tier 1 ("Pre-Trading Plan") risk context synchronously.
 *
 * Matches when:
 *   1. `riskSnapshot` is present, AND
 *   2. `accountId` equals `riskSnapshot.defaultAccountId`, AND
 *   3. A pair in `riskSnapshot.pairs` matches both `strategyId` and `playbookId`
 *
 * Returns the matched pair's `effectiveRisk` and the snapshot's
 * `selectedRiskProfileName`, or `null` if any condition fails
 * (caller proceeds to Tier 2 async resolution).
 *
 * Pure function — no DB calls, no side effects.
 */
export function resolveRiskContextTier1(params: {
  riskSnapshot: TradingSetupRiskSnapshot | undefined;
  accountId: string;
  strategyId: string;
  playbookId: string;
}): ResolvedRiskContext | null {
  const { riskSnapshot, accountId, strategyId, playbookId } = params;

  if (!riskSnapshot) return null;
  if (accountId !== riskSnapshot.defaultAccountId) return null;

  const matchedPair = riskSnapshot.pairs.find(
    (p) => p.strategyId === strategyId && p.playbookId === playbookId
  );
  if (!matchedPair) return null;

  // Resolve per-override deviation note ID from the winning override entry
  let resolvedNoteId: string | undefined;
  if (matchedPair.derivedFromStage === 'session') {
    if (matchedPair.derivedFromLevel === 'playbook-override') {
      resolvedNoteId = riskSnapshot.sessionPlaybookOverrides?.find(
        (o) => o.strategyId === strategyId && o.playbookId === playbookId
      )?.deviationNoteId;
    } else if (matchedPair.derivedFromLevel === 'strategy-override') {
      resolvedNoteId = riskSnapshot.sessionStrategyOverrides?.find(
        (o) => o.strategyId === strategyId
      )?.deviationNoteId;
    }
  }

  return {
    provenance: 'pre-trading-plan',
    risk: { type: matchedPair.effectiveRisk.type, value: matchedPair.effectiveRisk.value },
    profileName: riskSnapshot.selectedRiskProfileName,
    ...(matchedPair.derivedFromLevel ? { derivedFromLevel: matchedPair.derivedFromLevel } : {}),
    ...(matchedPair.derivedFromStage ? { derivedFromStage: matchedPair.derivedFromStage } : {}),
    ...(resolvedNoteId !== undefined ? { deviationNoteId: resolvedNoteId } : {}),
  };
}
